/**
 * ShellErrorBoundary — Top-level error boundary for shell apps.
 * Shows a full-page recovery UI when an unrecoverable error occurs.
 *
 * Includes automatic chunk-load recovery: when a lazy-loaded module fails
 * with a 404 (stale content hash after deploy), the page auto-reloads once
 * to fetch fresh HTML with correct asset references.
 *
 * @layer layout
 * @package @plerity/dashboard-shell
 */

import {
  clearChunkReloadFlag,
  isChunkLoadError,
  recoverFromChunkError,
  reportCaughtError,
} from "@plerity/observability";
import { OverflowRegion } from "@plerity/ui-core/OverflowRegion";
import React from "react";

/**
 * Detect response-shape validation failures thrown at the fetch boundary by
 * `useApiQuery({ schema })` (`@plerity/dashboard-shared`). Detection is by
 * `name` (mirroring the ChunkLoadError pattern) rather than `instanceof` so
 * it survives duplicated module instances across bundles.
 */
function isResponseValidationError(error: Error): error is Error & { url?: string } {
  return error.name === "ResponseValidationError";
}

/**
 * Classified error kind, computed once in getDerivedStateFromError so render()
 * can branch without re-sniffing the error each paint:
 *  - `chunk`   → stale hashed-bundle after a deploy (see isChunkLoadError)
 *  - `shape`   → response-shape validation failure (see isResponseValidationError)
 *  - `generic` → everything else
 */
type ShellErrorKind = "chunk" | "shape" | "generic";

interface ShellErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorKind: ShellErrorKind;
}

interface ShellErrorBoundaryProps {
  children: React.ReactNode;
  /** Shell name for error reporting */
  shellName: string;
}

export class ShellErrorBoundary extends React.Component<ShellErrorBoundaryProps, ShellErrorBoundaryState> {
  constructor(props: ShellErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorKind: "generic" };
  }

  static getDerivedStateFromError(error: Error): ShellErrorBoundaryState {
    const errorKind: ShellErrorKind = isChunkLoadError(error)
      ? "chunk"
      : isResponseValidationError(error)
        ? "shape"
        : "generic";
    return { hasError: true, error, errorKind };
  }

  componentDidMount(): void {
    // If the page loaded successfully after a chunk-reload, clear the flag
    // so future deploys can trigger auto-reload again.
    clearChunkReloadFlag();
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Auto-reload once on stale chunk errors (deploy mismatch)
    if (recoverFromChunkError(error, { label: `Shell:${this.props.shellName}` })) return;

    console.error(`[Shell:${this.props.shellName}] Unrecoverable error:`, error, errorInfo.componentStack);
    // Reaches observability.runtime_errors via the sink @plerity/runtime-reporter
    // registers at init (no-op if the shell never called initRuntimeReporter).
    reportCaughtError(error, {
      boundary: `shell:${this.props.shellName}`,
      componentStack: errorInfo.componentStack,
    });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorKind: "generic" });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const error = this.state.error;
    const { errorKind } = this.state;

    // Persistent stale-chunk case (chunk-load-recovery): a lazy module 404'd
    // because a new deploy purged the old hashed bundle. componentDidCatch
    // auto-reloads ONCE; if we still land here the reload didn't resolve it
    // (or is in-flight this paint), so show a calm "new version available"
    // message instead of the alarming generic crash UI. Only a hard reload
    // helps — "Try Again" (soft reset) would just re-throw, so it's omitted.
    if (errorKind === "chunk") {
      return (
        <div
          className="flex min-h-screen flex-col items-center justify-center gap-6 bg-surface-page p-8"
          role="alert"
          aria-labelledby="shell-error-heading"
          aria-describedby="shell-error-description"
        >
          <div className="flex flex-col items-center gap-4 rounded-xl border border-brand-subtle bg-surface p-10 shadow-lg">
            <svg
              className="h-12 w-12 text-brand-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="M21 12a9 9 0 1 1-2.64-6.36" />
              <polyline points="21 3 21 9 15 9" />
            </svg>
            <h1 id="shell-error-heading" className="text-xl font-semibold text-fg">
              A new version is available
            </h1>
            <p id="shell-error-description" className="max-w-md text-center text-sm text-gray-600 dark:text-gray-400">
              The {this.props.shellName} dashboard was updated while this tab was open, so some resources are out of
              date. Reload to load the latest version.
            </p>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={this.handleReload}
                className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus focus-visible:ring-offset-2"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Response-shape validation failure (Phase 6c, response-shape parity
    // campaign): distinct, attributable "data shape" UI so a backend
    // contract drift is never mistaken for an auth or network failure.
    const isShapeError = errorKind === "shape";
    const shapeErrorUrl =
      isShapeError && error !== null && typeof (error as { url?: unknown }).url === "string"
        ? (error as Error & { url: string }).url
        : null;

    const heading = isShapeError ? "Unexpected data shape" : "Something went wrong";
    const description = isShapeError
      ? `The ${this.props.shellName} dashboard received data in an unexpected shape${
          shapeErrorUrl ? ` from ${shapeErrorUrl}` : ""
        }. This is a data-contract issue — not a problem with your session or connection. Reloading may help; if it persists, the API response shape has drifted.`
      : `The ${this.props.shellName} dashboard encountered an unexpected error. You can try recovering or reload the page.`;

    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center gap-6 bg-surface-page p-8"
        role="alert"
        aria-labelledby="shell-error-heading"
        aria-describedby="shell-error-description"
      >
        {/* F-T3 (finance_app_quality_plan_2026-04-25): card was dark:bg-gray-900 — same as
            page bg in dark mode, so the elevated card disappeared. Bumped to dark:bg-gray-800
            (one step lighter than page) to restore visual elevation; the inset <pre> below
            steps back down to dark:bg-gray-900 to stay recessed inside the now-lighter card. */}
        <div className="flex flex-col items-center gap-4 rounded-xl border border-red-200 bg-surface p-10 shadow-lg dark:border-red-800">
          <svg
            className="h-12 w-12 text-red-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h1 id="shell-error-heading" className="text-xl font-semibold text-fg">
            {heading}
          </h1>
          <p id="shell-error-description" className="max-w-md text-center text-sm text-gray-600 dark:text-gray-400">
            {description}
          </p>
          {this.state.error && (
            <OverflowRegion
              axis="both"
              label="Error details"
              className="mt-2 max-w-lg rounded-md bg-gray-100 dark:bg-gray-900"
            >
              <pre className="p-3 text-xs text-gray-700 dark:text-gray-300">{this.state.error.message}</pre>
            </OverflowRegion>
          )}
          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={this.handleReset}
              className="rounded-md bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 dark:bg-gray-600 dark:text-gray-100 dark:hover:bg-gray-500"
            >
              Try Again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
